import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useState } from "react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";

const faqs = [
  {
    q: "How do I know which service is right for me?",
    a: "Book a free discovery call. We'll talk for 30 minutes, I'll ask about your business and I'll tell you what I honestly think makes sense. It might be the sprint or the full brand foundation work. And if what you need is something I don't offer, I'll tell you that too and point you somewhere better."
  },
  {
    q: "I've paid for branding before and nothing changed. Why would this be different?",
    a: "Most branding projects start with the visuals and skip the thinking. I start with the real questions: what does the business stand for, who is it talking to and where exactly is the message falling apart. The design comes after the strategy. That order is what makes it stick."
  },
  {
    q: "How long does each service take?",
    a: "The Brand Reality Check Sprint takes 7 business days. Brand Foundation usually runs 2 to 4 weeks. The full Brand Foundation & Visual Identity System takes around 2 months. Campaign timelines depend on scope and we agree on that before starting."
  },
  {
    q: "Do you work with businesses outside Poland?",
    a: "Yes. I work with founders across Europe and internationally. Everything works remotely and the quality doesn't drop. The sprint and workshop are designed for online sessions."
  },
  {
    q: "What if I'm still figuring out my offer?",
    a: "That's actually a good time to do the Brand Foundation work. Unclear offers and unclear brands tend to be tangled up in the same root issue. When we sort out who you are and who you're talking to, the offer usually gets sharper as a side effect."
  },
  {
    q: "Can I pay in installments?",
    a: "Yes. We can figure out a structure that works for you during the discovery call."
  },
  {
    q: "After the sprint, do I have to hire you for the next steps?",
    a: "No. The sprint gives you a plan you can follow on your own, with a team or with any other provider. If you want my help with implementation, we can talk about that. But there's zero obligation."
  },
  {
    q: "Do you make logos as a standalone service?",
    a: "No. A logo without strategy behind it is just a shape that looks nice for a while. If you need a visual identity, that comes as part of the Brand Foundation & Visual Identity System, built on top of the strategic work from the workshop."
  },
  {
    q: "Who does the execution work?",
    a: "The strategic thinking, brand diagnosis and creative direction is always me. For specific production tasks I bring in a small team of specialists I've handpicked and worked with for years. I trust their standards the same way I trust my own."
  },
];

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="border-b border-[#E8E3DE] last:border-0"
    >
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-6 text-left group">
        <span className="font-serif text-lg text-[#1C1210] pr-4 group-hover:text-[#BE123C] transition-colors duration-300">{item.q}</span>
        <ChevronDown size={20} className={`text-[#6B6560] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 pb-6" : "max-h-0"}`}>
        <p className="font-body text-sm text-[#6B6560] leading-relaxed max-w-2xl">{item.a}</p>
      </div>
    </motion.div>
  );
}

export default function FAQPage() {
  return (
    <main data-testid="faq-page">
      <section className="pt-32 pb-20 md:pb-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-12">
            <h1>
              <span className="font-display text-4xl md:text-5xl text-[#1C1210] block">QUESTIONS</span>
              <span className="font-accent text-4xl md:text-5xl text-[#BE123C] block mt-1">answered.</span>
            </h1>
          </motion.div>
          <div>
            {faqs.map((item, i) => (
              <FAQItem key={i} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-serif text-2xl md:text-3xl text-[#1C1210] mb-4">Still have questions?</h2>
            <p className="font-body text-base text-[#6B6560] mb-8">Book a free 30-minute call and I'll answer everything in person.</p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 rounded-full hover:bg-[#9F1239] transition-colors duration-300">
              BOOK A FREE CALL <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
